/**
 * KOPS-685: Deal Health Dashboard — Broker monitoring of deal_knowledge items
 *
 * Shows open/escalated compliance issues across all active deals.
 * Broker can dismiss or resolve items directly. Fed by the TC Orchestrator
 * and AI Deal Brief checkpoint pipeline.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle, faExclamationCircle, faInfoCircle,
  faCheckCircle, faTimesCircle, faSyncAlt, faChevronDown, faChevronRight,
  faUser, faMapMarkerAlt, faClock, faFileAlt, faShieldAlt,
  faArrowUp, faBell,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Colors (navy + gold)
// ---------------------------------------------------------------------------
const C = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db',
};

// ---------------------------------------------------------------------------
// Priority config
// ---------------------------------------------------------------------------
const PRIORITY_CONFIG: Record<string, { bg: string; text: string; icon: any; label: string }> = {
  critical: { bg: '#e74c3c', text: '#fff', icon: faExclamationCircle, label: 'Critical' },
  high:     { bg: '#e67e22', text: '#fff', icon: faExclamationTriangle, label: 'High' },
  medium:   { bg: '#f39c12', text: '#fff', icon: faInfoCircle, label: 'Medium' },
  low:      { bg: '#3498db', text: '#fff', icon: faInfoCircle, label: 'Low' },
};

const STATUS_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  open:        { bg: '#e74c3c20', text: '#e74c3c', label: 'Open' },
  in_progress: { bg: '#f39c1220', text: '#f39c12', label: 'In Progress' },
  escalated:   { bg: '#8e44ad20', text: '#8e44ad', label: 'Escalated' },
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface DealKnowledgeItem {
  id: string;
  category: string;
  name: string;
  description: string;
  priority: string;
  dueDate: string | null;
  daysOpen: number;
  action: string;
  actionType: string;
  status: string;
  source: string;
  sourceRef: string | null;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

interface DealGroup {
  dealId: string;
  clientName: string;
  agentName: string;
  agentEmail: string;
  agentId: string;
  transactionType: string;
  transactionStage: string;
  propertyAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyPrice: number | null;
  daysOpen: number;
  briefScore: number | null;
  briefCompleted: number | null;
  briefMissing: number | null;
  briefTotal: number | null;
  briefUpdatedAt: string | null;
  pendingReviewCount: number;
  approvedCount: number;
  acknowledged: boolean;
  acknowledgedAt: string | null;
  items: DealKnowledgeItem[];
  counts: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface HealthSummary {
  totalItems: number;
  totalOpen: number;
  totalInProgress: number;
  totalEscalated: number;
  dealsNeedingAttention: number;
  totalDeals: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const DealHealthDashboard: React.FC = () => {
  const [deals, setDeals] = useState<DealGroup[]>([]);
  const [summary, setSummary] = useState<HealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [dismissNotes, setDismissNotes] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [notingDealId, setNotingDealId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/broker/deal-health');
      if (data.success) {
        setDeals(data.deals || []);
        setSummary(data.summary || null);
        // Auto-expand deals with escalated items
        const autoExpand = new Set<string>();
        (data.deals || []).forEach((d: DealGroup) => {
          if (d.counts.critical > 0 || d.counts.high > 0) autoExpand.add(d.dealId);
        });
        setExpandedDeals(prev => new Set([...prev, ...autoExpand]));
      }
    } catch (err) {
      console.error('Failed to load deal health:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const toggleDeal = (dealId: string) => {
    setExpandedDeals(prev => {
      const next = new Set(prev);
      next.has(dealId) ? next.delete(dealId) : next.add(dealId);
      return next;
    });
  };

  // Extract dealId from brief-sourced item IDs (format: brief_DEALID_INDEX)
  const getDealIdFromItemId = (itemId: string) => {
    const match = itemId.match(/^brief_(.+)_\d+$/);
    return match ? match[1] : null;
  };

  const handleDismiss = async (itemId: string) => {
    const briefDealId = getDealIdFromItemId(itemId);
    if (briefDealId) {
      // Brief-sourced item — log as broker note, remove from local state
      try {
        await axiosInstance.post(`/broker/deal-health/${briefDealId}/note`, {
          note: `Broker dismissed: ${dismissNotes || 'Reviewed and dismissed'}`,
        });
        toast.success('Item dismissed');
        setDismissingId(null);
        setDismissNotes('');
        setDeals(prev => prev.map(d => d.dealId === briefDealId
          ? { ...d, items: d.items.filter(i => i.id !== itemId), counts: { ...d.counts, total: d.counts.total - 1 } }
          : d
        ));
      } catch { toast.error('Failed to dismiss'); }
      return;
    }
    try {
      await axiosInstance.patch(`/broker/deal-health/${itemId}/dismiss`, { notes: dismissNotes || 'Dismissed by broker' });
      toast.success('Item dismissed');
      setDismissingId(null);
      setDismissNotes('');
      fetchHealth();
    } catch {
      toast.error('Failed to dismiss');
    }
  };

  const handleSendNote = async (dealId: string) => {
    if (!noteText.trim()) return;
    try {
      await axiosInstance.post(`/broker/deal-health/${dealId}/note`, { note: noteText.trim() });
      toast.success('Note sent to deal');
      setNotingDealId(null);
      setNoteText('');
    } catch {
      toast.error('Failed to send note');
    }
  };

  const handleResolve = async (itemId: string) => {
    const briefDealId = getDealIdFromItemId(itemId);
    if (briefDealId) {
      // Brief-sourced item — log as broker note, remove from local state
      try {
        await axiosInstance.post(`/broker/deal-health/${briefDealId}/note`, {
          note: 'Broker reviewed and resolved action item',
        });
        toast.success('Item resolved');
        setDeals(prev => prev.map(d => d.dealId === briefDealId
          ? { ...d, items: d.items.filter(i => i.id !== itemId), counts: { ...d.counts, total: d.counts.total - 1 } }
          : d
        ));
      } catch { toast.error('Failed to resolve'); }
      return;
    }
    try {
      await axiosInstance.patch(`/broker/deal-health/${itemId}/resolve`, { notes: 'Manually resolved by broker' });
      toast.success('Item resolved');
      fetchHealth();
    } catch {
      toast.error('Failed to resolve');
    }
  };

  // Filter deals by priority
  const filteredDeals = filterPriority === 'all'
    ? deals
    : deals.filter(d => d.items.some(i => i.priority === filterPriority)).map(d => ({
        ...d,
        items: d.items.filter(i => i.priority === filterPriority),
      }));

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>
        <FontAwesomeIcon icon={faSyncAlt} spin style={{ fontSize: 20, marginBottom: 8 }} />
        <div style={{ fontSize: 13 }}>Loading deal health...</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* ── Summary Strip ── */}
      {summary && (
        <div style={{
          display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap',
        }}>
          {[
            { label: 'Active Deals', value: summary.totalDeals, color: C.blue, icon: faFileAlt },
            { label: 'Open Items', value: summary.totalOpen, color: C.orange, icon: faClock },
            { label: 'In Progress', value: summary.totalInProgress, color: C.gold, icon: faSyncAlt },
            { label: 'Escalated', value: summary.totalEscalated, color: C.red, icon: faArrowUp },
            { label: 'Needs Attention', value: summary.dealsNeedingAttention, color: '#8e44ad', icon: faBell },
          ].map(s => (
            <div key={s.label} style={{
              flex: '1 1 140px', background: '#fff', border: '1px solid #e9ecef',
              borderRadius: 8, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: `${s.color}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FontAwesomeIcon icon={s.icon} style={{ color: s.color, fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: C.navy }}>{s.value}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Controls ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'critical', 'high', 'medium', 'low'].map(p => (
            <button
              key={p}
              onClick={() => setFilterPriority(p)}
              style={{
                padding: '5px 12px', borderRadius: 5, border: '1px solid #e9ecef',
                background: filterPriority === p ? C.navy : '#fff',
                color: filterPriority === p ? C.gold : C.navy,
                fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
              }}
            >
              {p === 'all' ? 'All' : p}
            </button>
          ))}
        </div>
        <button
          onClick={() => { setLoading(true); fetchHealth(); }}
          style={{
            padding: '5px 12px', borderRadius: 5, border: `1px solid ${C.gold}`,
            background: 'transparent', color: C.gold, fontSize: 11, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <FontAwesomeIcon icon={faSyncAlt} /> Refresh
        </button>
      </div>

      {/* ── Empty State ── */}
      {filteredDeals.length === 0 && (
        <div style={{
          background: '#fff', border: '1px solid #e9ecef', borderRadius: 8,
          padding: '40px 20px', textAlign: 'center',
        }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 32, color: C.green, marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 4 }}>All Clear</div>
          <div style={{ fontSize: 12, color: C.muted }}>No open compliance items across your deals.</div>
        </div>
      )}

      {/* ── Deal Groups ── */}
      {filteredDeals.map(deal => {
        const expanded = expandedDeals.has(deal.dealId);
        const hasCritical = deal.counts.critical > 0;
        const borderColor = hasCritical ? C.red : deal.counts.high > 0 ? C.orange : C.gold;

        return (
          <div key={deal.dealId} style={{
            background: '#fff', border: '1px solid #e9ecef', borderLeft: `4px solid ${borderColor}`,
            borderRadius: 8, marginBottom: 12, overflow: 'hidden',
          }}>
            {/* ── Deal Header ── */}
            <div
              onClick={() => toggleDeal(deal.dealId)}
              style={{
                padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
                cursor: 'pointer', background: expanded ? '#fafbfc' : 'transparent',
              }}
            >
              <FontAwesomeIcon
                icon={expanded ? faChevronDown : faChevronRight}
                style={{ color: C.muted, fontSize: 11, width: 12 }}
              />

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.navy }}>
                    {deal.clientName}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                    background: deal.transactionType === 'listing' ? '#27ae6020' : '#3498db20',
                    color: deal.transactionType === 'listing' ? '#27ae60' : '#3498db',
                    textTransform: 'uppercase',
                  }}>
                    {deal.transactionType}
                  </span>
                  {deal.briefScore !== null && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                      background: deal.briefScore >= 80 ? '#27ae6020' : deal.briefScore >= 50 ? '#f39c1220' : '#e74c3c20',
                      color: deal.briefScore >= 80 ? '#27ae60' : deal.briefScore >= 50 ? '#f39c12' : '#e74c3c',
                    }}>
                      {deal.briefScore}% compliant
                    </span>
                  )}
                  {deal.acknowledged && (
                    <span style={{ fontSize: 10, fontWeight: 600, color: '#27ae60' }}>✓ Acknowledged</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: C.muted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span>
                    <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 4, color: C.gold }} />
                    {deal.propertyAddress}{deal.propertyCity ? `, ${deal.propertyCity}` : ''}
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: 4 }} />
                    {deal.agentName}
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faClock} style={{ marginRight: 4 }} />
                    Day {deal.daysOpen}
                  </span>
                  {deal.pendingReviewCount > 0 && (
                    <span style={{ color: C.orange, fontWeight: 600 }}>
                      <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 4 }} />
                      {deal.pendingReviewCount} docs pending review
                    </span>
                  )}
                </div>
              </div>

              {/* AI action item counts */}
              <div style={{ display: 'flex', gap: 6 }}>
                {deal.counts.critical > 0 && (
                  <span style={{ ...badgeStyle, background: '#e74c3c', color: '#fff' }}>
                    {deal.counts.critical} critical
                  </span>
                )}
                {deal.counts.high > 0 && (
                  <span style={{ ...badgeStyle, background: '#e67e22', color: '#fff' }}>
                    {deal.counts.high} high
                  </span>
                )}
                {deal.items.length === 0 && deal.pendingReviewCount > 0 && (
                  <span style={{ ...badgeStyle, background: C.orange, color: '#fff' }}>
                    review needed
                  </span>
                )}
              </div>
            </div>

            {/* ── Expanded Items ── */}
            {expanded && (
              <div style={{ borderTop: '1px solid #eee', padding: '8px 16px 12px' }}>
                {deal.items.map(item => {
                  const pc = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;
                  const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.open;

                  return (
                    <div key={item.id} style={{
                      padding: '10px 12px', marginBottom: 6, borderRadius: 6,
                      background: '#f8f9fa', border: '1px solid #eee',
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                    }}>
                      {/* Priority icon */}
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', background: `${pc.bg}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        marginTop: 2,
                      }}>
                        <FontAwesomeIcon icon={pc.icon} style={{ color: pc.bg, fontSize: 12 }} />
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                            background: pc.bg, color: pc.text, textTransform: 'uppercase',
                          }}>
                            {pc.label}
                          </span>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                            background: sc.bg, color: sc.text,
                          }}>
                            {sc.label}
                          </span>
                          <span style={{ fontSize: 10, color: C.muted }}>
                            <FontAwesomeIcon icon={faClock} style={{ marginRight: 3 }} />
                            {item.daysOpen}d open
                          </span>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.navy, marginBottom: 2 }}>
                          {item.description}
                        </div>
                        {item.action && (
                          <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>
                            <strong>Action:</strong> {item.action}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {dismissingId === item.id ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <input
                              type="text"
                              placeholder="Notes (optional)"
                              value={dismissNotes}
                              onChange={e => setDismissNotes(e.target.value)}
                              style={{
                                padding: '4px 8px', fontSize: 10, borderRadius: 4,
                                border: '1px solid #ddd', width: 140,
                              }}
                            />
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button
                                onClick={() => handleDismiss(item.id)}
                                style={{ ...actionBtnStyle, background: C.muted, color: '#fff' }}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => { setDismissingId(null); setDismissNotes(''); }}
                                style={{ ...actionBtnStyle, background: '#eee', color: '#666' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleResolve(item.id)}
                              title="Mark as resolved"
                              style={{ ...actionBtnStyle, background: `${C.green}15`, color: C.green }}
                            >
                              <FontAwesomeIcon icon={faCheckCircle} />
                            </button>
                            <button
                              onClick={() => setDismissingId(item.id)}
                              title="Dismiss"
                              style={{ ...actionBtnStyle, background: '#f0f0f0', color: '#999' }}
                            >
                              <FontAwesomeIcon icon={faTimesCircle} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Broker quick note */}
                {notingDealId === deal.dealId ? (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <textarea
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      placeholder="Send a note to this deal (agent will see this)..."
                      rows={2}
                      style={{
                        flex: 1, padding: '8px 10px', fontSize: 11, borderRadius: 6,
                        border: '1px solid #ddd', resize: 'vertical', fontFamily: 'inherit',
                        outline: 'none',
                      }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button
                        onClick={() => handleSendNote(deal.dealId)}
                        disabled={!noteText.trim()}
                        style={{
                          ...actionBtnStyle, background: C.gold, color: C.navy,
                          opacity: noteText.trim() ? 1 : 0.5,
                        }}
                      >
                        Send
                      </button>
                      <button
                        onClick={() => { setNotingDealId(null); setNoteText(''); }}
                        style={{ ...actionBtnStyle, background: '#eee', color: '#666' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setNotingDealId(deal.dealId)}
                    style={{
                      marginTop: 8, padding: '6px 14px', border: `1px solid ${C.gold}`,
                      borderRadius: 5, background: 'transparent', color: C.gold,
                      fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Send Note to Deal
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------
const badgeStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
};

const actionBtnStyle: React.CSSProperties = {
  padding: '4px 8px', border: 'none', borderRadius: 4, fontSize: 11,
  fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
};

export default DealHealthDashboard;
